import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  from,
  map,
  switchMap,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  AyaConsultMode,
  AyaConversationDto,
  AyaLane,
  AyaMessageDto,
  AyaRunDto,
  AyaRunStatusDto,
  DEFAULT_AYA_CONSULT_MODE,
} from '../models/aya-conversation.model';
import {
  AyaStreamHandlers,
  streamAyaRun,
} from '../utils/aya-stream.util';
import {
  ChatConversation,
  ChatHistoryGroup,
  ChatMessage,
} from '../models/chat-history.model';
import { ApplicationContextService } from './application-context.service';
import { AuthenticationService } from './auth.service';

/** Same key as ask-aya-health for locally renamed titles. */
const TITLES_KEY = 'aya.conversationTitles.v1';

/**
 * ask-aya Work tab private workspace org (X-Aya-Org-Id when no org is selected).
 * Clinic uses the logged-in user's companyId when present (Work / Organization lane).
 */
export const AYA_WORK_ORG_ID = '27a8906a-a8ab-415f-9128-029901816a92';

@Injectable({ providedIn: 'root' })
export class AyaConversationsService {
  private readonly conversationsSubject = new BehaviorSubject<ChatConversation[]>(
    []
  );
  private readonly searchQuerySubject = new BehaviorSubject<string>('');
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);

  readonly conversations$ = this.conversationsSubject.asObservable();
  readonly searchQuery$ = this.searchQuerySubject.asObservable();
  readonly loading$ = this.loadingSubject.asObservable();

  /** In-memory message cache for opened Aya threads (scoped by lane). */
  private messageCache = new Map<string, ChatMessage[]>();
  private activeLaneKey = '';

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthenticationService,
    private readonly applicationContextService: ApplicationContextService
  ) {
    this.applicationContextService.context
      .pipe(
        map((ctx) => this.resolveOrgIdFromContext(ctx)),
        filter(Boolean),
        distinctUntilChanged(),
        switchMap(() => from(this.refresh()))
      )
      .subscribe();
  }

  /**
   * Ask Aya BFF on the Aya app host (`{ayaAppUrl}/api/aya` → upstream hx-aya-v2).
   */
  get apiBase(): string {
    const configured = environment.ayaApiBaseUrl?.trim();
    if (configured) {
      return configured.replace(/\/+$/, '');
    }
    const appUrl = environment.ayaAppUrl?.trim();
    if (appUrl) {
      return `${appUrl.replace(/\/+$/, '')}/api/aya`;
    }
    return '/api/aya';
  }

  searchConversations(query: string): void {
    this.searchQuerySubject.next(query);
  }

  getGroupedConversations(): Observable<ChatHistoryGroup[]> {
    return this.conversations$.pipe(
      map((conversations) => this.groupConversationsByPeriod(conversations))
    );
  }

  getFilteredConversations(): Observable<ChatConversation[]> {
    return combineLatest([this.conversations$, this.searchQuery$]).pipe(
      map(([conversations, query]) => {
        const q = query.trim().toLowerCase();
        if (!q) {
          return conversations;
        }
        return conversations.filter((c) => c.title.toLowerCase().includes(q));
      })
    );
  }

  getConversation(id: string): ChatConversation | undefined {
    return this.conversationsSubject.value.find((c) => c.id === id);
  }

  async refresh(): Promise<void> {
    this.loadingSubject.next(true);
    try {
      const lane = await this.resolveWorkLane();
      if (!lane) {
        this.conversationsSubject.next([]);
        return;
      }
      this.resetCachesIfLaneChanged(lane);
      const rows = await this.listConversations(lane, 200);
      const titles = this.readTitleOverrides();
      const mapped = rows
        .filter((c) => !c.deleted_at)
        .map((c) => this.toChatConversation(c, titles, lane.userId))
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      this.conversationsSubject.next(mapped);
    } catch (err) {
      console.warn('[clinic-aya] Failed to load conversations', err);
      this.conversationsSubject.next([]);
    } finally {
      this.loadingSubject.next(false);
    }
  }

  /** Load thread metadata when the list cache does not contain the id yet. */
  async fetchConversation(conversationId: string): Promise<ChatConversation | null> {
    const lane = await this.resolveWorkLane();
    if (!lane) {
      return null;
    }
    const path = `/private/conversations/${encodeURIComponent(conversationId)}`;
    try {
      const row = await this.ayaGet<AyaConversationDto>(lane, path);
      if (!row?.id || row.deleted_at) {
        return null;
      }
      const titles = this.readTitleOverrides();
      return this.toChatConversation(row, titles, lane.userId);
    } catch (err) {
      console.warn('[clinic-aya] Failed to fetch conversation', conversationId, err);
      return null;
    }
  }

  async getWorkLane(): Promise<AyaLane | null> {
    return this.resolveWorkLane();
  }

  async createConversation(title = 'New chat'): Promise<AyaConversationDto> {
    const lane = await this.resolveWorkLane();
    if (!lane) {
      throw new Error('Sign in required to start a chat.');
    }
    return this.ayaPost<AyaConversationDto>(lane, '/private/conversations', {
      title,
    });
  }

  async postMessage(
    conversationId: string,
    content: string,
    consultMode: AyaConsultMode = DEFAULT_AYA_CONSULT_MODE
  ): Promise<AyaRunDto> {
    const lane = await this.resolveWorkLane();
    if (!lane) {
      throw new Error('Sign in required to send a message.');
    }
    const path = `/private/conversations/${encodeURIComponent(conversationId)}/messages`;
    return this.ayaPost<AyaRunDto>(lane, path, {
      content,
      consult_mode: consultMode,
    });
  }

  async editMessage(messageId: string, content: string): Promise<AyaRunDto> {
    const lane = await this.resolveWorkLane();
    if (!lane) {
      throw new Error('Sign in required to edit a message.');
    }
    const path = `/private/messages/${encodeURIComponent(messageId)}/edit`;
    return this.ayaPost<AyaRunDto>(lane, path, { content });
  }

  async getRunStatus(runId: string): Promise<AyaRunStatusDto> {
    const lane = await this.resolveWorkLane();
    if (!lane) {
      throw new Error('Sign in required.');
    }
    const path = `/private/runs/${encodeURIComponent(runId)}`;
    return this.ayaGet<AyaRunStatusDto>(lane, path);
  }

  async stopRun(runId: string): Promise<void> {
    const lane = await this.resolveWorkLane();
    if (!lane) {
      return;
    }
    const path = `/private/runs/${encodeURIComponent(runId)}/stop`;
    await this.ayaPost<void>(lane, path, {});
  }

  async streamRun(
    run: AyaRunDto,
    handlers: AyaStreamHandlers,
    signal?: AbortSignal
  ): Promise<void> {
    const lane = await this.resolveWorkLane();
    if (!lane) {
      throw new Error('Sign in required.');
    }
    await streamAyaRun(
      {
        apiBase: this.apiBase,
        lane,
        getAuthHeaders: async () => {
          const token = await this.authService.getValidAccessToken();
          return token ? { Authorization: `Bearer ${token}` } : {};
        },
        getRunStatus: (runId) => this.getRunStatus(runId),
      },
      run.stream_url,
      run.run_id,
      handlers,
      signal
    );
  }

  async loadMessages(conversationId: string, force = false): Promise<ChatMessage[]> {
    const cacheKey = this.messageCacheKey(conversationId);
    if (!force) {
      const cached = this.messageCache.get(cacheKey);
      if (cached) {
        return cached;
      }
    }

    const lane = await this.resolveWorkLane();
    if (!lane) {
      return [];
    }

    const path = `/private/conversations/${encodeURIComponent(
      conversationId
    )}/messages`;
    const rows = await this.ayaGet<AyaMessageDto[]>(lane, path);
    const messages = (Array.isArray(rows) ? rows : []).map((m) =>
      this.toChatMessage(m)
    );
    this.messageCache.set(cacheKey, messages);
    return messages;
  }

  deleteConversation(id: string): void {
    this.conversationsSubject.next(
      this.conversationsSubject.value.filter((c) => c.id !== id)
    );
    this.messageCache.delete(this.messageCacheKey(id));
    void this.resolveWorkLane().then(async (lane) => {
      if (!lane) {
        return;
      }
      const path = `/private/conversations/${encodeURIComponent(id)}`;
      return this.ayaDelete(lane, path).catch(() => undefined);
    });
  }

  private async listConversations(
    lane: AyaLane,
    limit: number
  ): Promise<AyaConversationDto[]> {
    const path = `/private/conversations?limit=${limit}`;
    const data = await this.ayaGet<AyaConversationDto[]>(lane, path);
    return Array.isArray(data) ? data : [];
  }

  /**
   * Work lane for Clinic — same headers as ask-aya Work tab:
   * X-Profile: professional, X-Aya-Org-Id, X-Aya-User-Id, Authorization Bearer (Firebase).
   */
  private async resolveWorkLane(): Promise<AyaLane | null> {
    const token = await this.authService.getValidAccessToken();
    if (!token) {
      return null;
    }
    const payload = this.parseJwtPayload(token);
    const userId = String(payload?.['sub'] ?? payload?.['user_id'] ?? '').trim();
    if (!userId) {
      return null;
    }
    const orgId =
      this.resolveOrgIdFromContext(
        this.applicationContextService.contextSnapshot
      ) || AYA_WORK_ORG_ID;
    return {
      profile: 'professional',
      orgId,
      userId,
    };
  }

  private resolveOrgIdFromContext(ctx: {
    currentlyLoggedInUser?: { companyId?: string };
  } | null): string {
    return (
      ctx?.currentlyLoggedInUser?.companyId?.trim() ||
      environment.preferredClinicCompanyId?.trim() ||
      ''
    );
  }

  private async buildHeaders(lane: AyaLane): Promise<HttpHeaders> {
    const token = await this.authService.getValidAccessToken();
    let headers = laneHeaders(lane);
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  private async ayaPost<T>(
    lane: AyaLane,
    path: string,
    body: unknown
  ): Promise<T> {
    const url = `${this.apiBase}${path.startsWith('/') ? path : `/${path}`}`;
    const send = async (retried: boolean): Promise<T> => {
      const headers = (await this.buildHeaders(lane)).set(
        'Content-Type',
        'application/json'
      );
      try {
        return await firstValueFrom(this.http.post<T>(url, body, { headers }));
      } catch (err) {
        if (!retried && err instanceof HttpErrorResponse && err.status === 401) {
          await this.authService.refreshSession('aya-private-401');
          return send(true);
        }
        throw err;
      }
    };
    return send(false);
  }

  private async ayaGet<T>(lane: AyaLane, path: string): Promise<T> {
    const url = `${this.apiBase}${path.startsWith('/') ? path : `/${path}`}`;
    const send = async (retried: boolean): Promise<T> => {
      const headers = await this.buildHeaders(lane);
      try {
        return await firstValueFrom(this.http.get<T>(url, { headers }));
      } catch (err) {
        if (!retried && err instanceof HttpErrorResponse && err.status === 401) {
          await this.authService.refreshSession('aya-private-401');
          return send(true);
        }
        throw err;
      }
    };
    return send(false);
  }

  private async ayaDelete(lane: AyaLane, path: string): Promise<void> {
    const url = `${this.apiBase}${path.startsWith('/') ? path : `/${path}`}`;
    const send = async (retried: boolean): Promise<void> => {
      const headers = await this.buildHeaders(lane);
      try {
        await firstValueFrom(this.http.delete<void>(url, { headers }));
      } catch (err) {
        if (!retried && err instanceof HttpErrorResponse && err.status === 401) {
          await this.authService.refreshSession('aya-private-401');
          return send(true);
        }
        throw err;
      }
    };
    return send(false);
  }

  private resetCachesIfLaneChanged(lane: AyaLane): void {
    const key = ayaLaneKey(lane);
    if (this.activeLaneKey && this.activeLaneKey !== key) {
      this.messageCache.clear();
    }
    this.activeLaneKey = key;
  }

  private messageCacheKey(conversationId: string): string {
    return `${this.activeLaneKey}:${conversationId}`;
  }

  private toChatConversation(
    row: AyaConversationDto,
    titles: Record<string, string>,
    userId: string
  ): ChatConversation {
    return {
      id: row.id,
      title: titles[row.id] || row.title?.trim() || 'New chat',
      lastMessage: '',
      timestamp: new Date(row.updated_at || row.created_at),
      messages: [],
      userId,
    };
  }

  private toChatMessage(row: AyaMessageDto): ChatMessage {
    const role = String(row.role || '').toLowerCase();
    return {
      id: row.id,
      content: row.content || '',
      isUser: role === 'user' || role === 'human',
      timestamp: new Date(row.created_at),
      conversationId: row.conversation_id,
    };
  }

  private readTitleOverrides(): Record<string, string> {
    try {
      const raw = localStorage.getItem(TITLES_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object'
        ? (parsed as Record<string, string>)
        : {};
    } catch {
      return {};
    }
  }

  private parseJwtPayload(token: string): Record<string, unknown> | null {
    try {
      const parts = token.split('.');
      if (parts.length < 2) {
        return null;
      }
      const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(json) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  private groupConversationsByPeriod(
    conversations: ChatConversation[]
  ): ChatHistoryGroup[] {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const lastMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    const groups: ChatHistoryGroup[] = [];

    const pushGroup = (period: string, items: ChatConversation[]) => {
      if (items.length) {
        groups.push({ period, conversations: items });
      }
    };

    pushGroup(
      'Today',
      conversations.filter((c) => c.timestamp >= today)
    );
    pushGroup(
      'Yesterday',
      conversations.filter((c) => c.timestamp >= yesterday && c.timestamp < today)
    );
    pushGroup(
      'Last Week',
      conversations.filter(
        (c) => c.timestamp >= lastWeek && c.timestamp < yesterday
      )
    );
    pushGroup(
      'Last Month',
      conversations.filter(
        (c) => c.timestamp >= lastMonth && c.timestamp < lastWeek
      )
    );

    const older = conversations.filter((c) => c.timestamp < lastMonth);
    if (older.length) {
      const monthGroups: Record<string, ChatConversation[]> = {};
      older.forEach((conversation) => {
        const monthKey = conversation.timestamp.toLocaleDateString('en-US', {
          month: 'long',
          year: 'numeric',
        });
        monthGroups[monthKey] = monthGroups[monthKey] || [];
        monthGroups[monthKey].push(conversation);
      });
      Object.keys(monthGroups).forEach((month) => {
        pushGroup(month, monthGroups[month]);
      });
    }

    return groups;
  }
}

function laneHeaders(lane: AyaLane): HttpHeaders {
  return new HttpHeaders({
    'X-Profile': lane.profile,
    'X-Aya-User-Id': lane.userId,
    'X-Aya-Org-Id': lane.orgId,
  });
}

function ayaLaneKey(lane: AyaLane): string {
  return `${lane.profile}:${lane.orgId}:${lane.userId}`;
}
